export const MAX_NATIONAL_DEX_ID = 1025;

export const STATS = [
  { key: 'hp', label: 'HP' },
  { key: 'attack', label: 'Attack' },
  { key: 'defense', label: 'Defense' },
  { key: 'specialAttack', label: 'Sp. Atk' },
  { key: 'specialDefense', label: 'Sp. Def' },
  { key: 'speed', label: 'Speed' },
];

const NAME_OVERRIDES = {
  'mr-mime': 'Mr. Mime',
  'mime-jr': 'Mime Jr.',
  'mr-rime': 'Mr. Rime',
  'type-null': 'Type: Null',
  'ho-oh': 'Ho-Oh',
  'porygon-z': 'Porygon-Z',
  'jangmo-o': 'Jangmo-o',
  'hakamo-o': 'Hakamo-o',
  'kommo-o': 'Kommo-o',
  'nidoran-f': 'Nidoran♀',
  'nidoran-m': 'Nidoran♂',
  farfetchd: "Farfetch'd",
  sirfetchd: "Sirfetch'd",
  flabebe: 'Flabébé',
  'tapu-koko': 'Tapu Koko',
  'tapu-lele': 'Tapu Lele',
  'tapu-bulu': 'Tapu Bulu',
  'tapu-fini': 'Tapu Fini',
};

// [background, text] pairs chosen so the label stays readable on the badge.
export const TYPE_COLORS = {
  normal: ['#A8A77A', '#1c1915'],
  fire: ['#EE8130', '#1c1915'],
  water: ['#6390F0', '#0d1b33'],
  electric: ['#F7D02C', '#1c1915'],
  grass: ['#63B336', '#10210c'],
  ice: ['#7FCFCB', '#102423'],
  fighting: ['#C22E28', '#fffaf3'],
  poison: ['#A33EA1', '#fffaf3'],
  ground: ['#D4AE4A', '#1c1915'],
  flying: ['#A98FF3', '#1c1233'],
  psychic: ['#F95587', '#2a0b16'],
  bug: ['#A6B91A', '#1c1915'],
  rock: ['#B6A136', '#1c1915'],
  ghost: ['#735797', '#fffaf3'],
  dragon: ['#6F35FC', '#fffaf3'],
  dark: ['#705746', '#fffaf3'],
  steel: ['#9A9AB4', '#1c1915'],
  fairy: ['#D685AD', '#2a1220'],
};

export function toQuery(value) {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  if (/^\d+$/.test(raw)) {
    const id = Number(raw);
    return id >= 1 ? String(id) : '';
  }
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['.]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function formatName(name) {
  if (!name) return '';
  const key = String(name).toLowerCase();
  if (NAME_OVERRIDES[key]) return NAME_OVERRIDES[key];
  // A published dex entry already uses the display name, such as Charizard or Mr. Mime.
  if (name !== key) return name;
  return key
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function formatDexNumber(id) {
  if (!id && id !== 0) return '';
  return id < 1000 ? `#${String(id).padStart(3, '0')}` : `#${id}`;
}

export function formatHeight(decimeters) {
  if (typeof decimeters !== 'number') return '—';
  return `${(decimeters / 10).toFixed(1)} m`;
}

export function formatWeight(hectograms) {
  if (typeof hectograms !== 'number') return '—';
  return `${(hectograms / 10).toFixed(1)} kg`;
}

export function isPokemon(pokemon) {
  return Boolean(
    pokemon &&
      typeof pokemon.id === 'number' &&
      typeof pokemon.name === 'string' &&
      pokemon.stats &&
      typeof pokemon.stats.specialAttack === 'number' &&
      Array.isArray(pokemon.types)
  );
}

export function summarizeMatchup(pokemon1, pokemon2) {
  const stats = {};
  let pokemon1Wins = 0;
  let pokemon2Wins = 0;
  let ties = 0;

  STATS.forEach(({ key }) => {
    const left = pokemon1.stats[key];
    const right = pokemon2.stats[key];
    if (left > right) {
      stats[key] = 'pokemon1';
      pokemon1Wins += 1;
    } else if (right > left) {
      stats[key] = 'pokemon2';
      pokemon2Wins += 1;
    } else {
      stats[key] = 'tie';
      ties += 1;
    }
  });

  let total = 'tie';
  if (pokemon1.total > pokemon2.total) total = 'pokemon1';
  if (pokemon2.total > pokemon1.total) total = 'pokemon2';

  return { stats, pokemon1Wins, pokemon2Wins, ties, total };
}

export function matchupHeadline(pokemon1, pokemon2, summary) {
  const left = formatName(pokemon1.name);
  const right = formatName(pokemon2.name);
  if (summary.ties === STATS.length) {
    return `${left} and ${right} match on every stat`;
  }
  if (summary.pokemon1Wins === summary.pokemon2Wins) {
    return `Even split, ${summary.pokemon1Wins}–${summary.pokemon2Wins}`;
  }
  const leader = summary.pokemon1Wins > summary.pokemon2Wins ? left : right;
  const hi = Math.max(summary.pokemon1Wins, summary.pokemon2Wins);
  const lo = Math.min(summary.pokemon1Wins, summary.pokemon2Wins);
  return `${leader} takes ${hi}–${lo} on base stats`;
}

export function totalLine(pokemon1, pokemon2, summary) {
  const left = formatName(pokemon1.name);
  const right = formatName(pokemon2.name);
  if (summary.total === 'tie') {
    return `Base stat total is tied at ${pokemon1.total}.`;
  }
  const leader = summary.total === 'pokemon1' ? left : right;
  const hi = Math.max(pokemon1.total, pokemon2.total);
  const lo = Math.min(pokemon1.total, pokemon2.total);
  return `${leader} has the higher base stat total, ${hi} to ${lo}.`;
}

export function ribbonsFor(slot, summary) {
  if (!summary) return [];
  const wins = slot === 'pokemon1' ? summary.pokemon1Wins : summary.pokemon2Wins;
  const other = slot === 'pokemon1' ? summary.pokemon2Wins : summary.pokemon1Wins;
  const ribbons = [];
  if (wins > other) ribbons.push('Stat lead');
  if (summary.total === slot) ribbons.push('Higher total');
  return ribbons;
}

export function adjacentId(id, delta, max = MAX_NATIONAL_DEX_ID) {
  const next = Number(id) + delta;
  if (!Number.isInteger(next) || next < 1 || next > max) return null;
  return next;
}
